import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { toast } from 'react-hot-toast';
import i18n from '../i18n';

// Lookups read i18n.language at notification time (rather than a captured
// useTranslation `t`) so handlers fire in the user's current language even if
// they switch after the EventSource subscription was set up.
const tr = (key) => i18n.t(`common:${key}`);

const NotificationContext = createContext({});

export const NotificationProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [eventSource, setEventSource] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const reconnectTimeoutRef = React.useRef(null);
  const eventSourceRef = React.useRef(null);
  const isConnectingRef = React.useRef(false);

  useEffect(() => {
    // Clear any existing reconnect timeout
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    if (!isAuthenticated) {
      // Close existing connection
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }

      setEventSource(null);
      setIsConnected(false);
      isConnectingRef.current = false;
      return;
    }

    // Prevent multiple concurrent connections
    if (isConnectingRef.current || eventSourceRef.current) {
      return;
    }

    const token = localStorage.getItem('access_token');
    if (!token) {
      setIsConnected(false);
      return;
    }

    isConnectingRef.current = true;

    const es = new EventSource(`/api/notifications/subscribe/manager?token=${token}`);
    eventSourceRef.current = es;

    let isCleanedUp = false;

    es.onopen = () => {
      if (isCleanedUp) return;
      setIsConnected(true);
      isConnectingRef.current = false;
    };

    es.onerror = (error) => {
      if (isCleanedUp) return;
      setIsConnected(false);
      isConnectingRef.current = false;
      eventSourceRef.current = null;
      es.close();

      // Try to reconnect after 5 seconds
      reconnectTimeoutRef.current = setTimeout(() => {
        // Re-trigger the effect by forcing a state update
        setIsConnected(prev => prev);
      }, 5000);
    };

    // Connection event from server
    es.addEventListener('connection', (event) => {
      setIsConnected(true);
      const data = JSON.parse(event.data);
      toast.success(data.message || tr('notification.connected'), {
        duration: 2000,
        position: 'bottom-right',
      });
    });

    // Heartbeat to keep connection alive
    es.addEventListener('heartbeat', (event) => {
      // Keep connection alive
    });

    // ============================================
    // Ticket Events
    // ============================================
    es.addEventListener('new_ticket', (event) => {
      const data = JSON.parse(event.data);
      const notification = {
        id: Date.now(),
        type: 'ticket',
        title: 'New Ticket',
        message: `Ticket #${data.ticket_id} has been created`,
        timestamp: new Date(),
        data
      };
      setNotifications(prev => [notification, ...prev]);
      toast.success(notification.message, { duration: 5000, position: 'top-right' });
    });

    es.addEventListener('ticket_created', (event) => {
      const data = JSON.parse(event.data);
      const notification = {
        id: Date.now(),
        type: 'ticket',
        title: 'New Support Ticket',
        message: `${data.renter_name || tr('notification.renterFallback')} submitted: ${data.title}`,
        timestamp: new Date(),
        actionUrl: `/admin/tickets/${data.ticket_id || data.ticket_uuid}`,
        data
      };
      setNotifications(prev => [notification, ...prev]);
      toast.success(notification.message, { duration: 5000, position: 'top-right', icon: '🎫' });
    });

    es.addEventListener('ticket_update', (event) => {
      const data = JSON.parse(event.data);
      const notification = {
        id: Date.now(),
        type: 'ticket',
        title: 'Ticket Updated',
        message: data.message || `Ticket #${data.ticket_id} updated`,
        timestamp: new Date(),
        data
      };
      setNotifications(prev => [notification, ...prev]);
      toast(notification.message, { duration: 5000, position: 'top-right', icon: '🎫' });
    });

    es.addEventListener('ticket_reply', (event) => {
      const data = JSON.parse(event.data);
      const notification = {
        id: Date.now(),
        type: 'ticket',
        title: 'New Ticket Reply',
        message: `Renter replied to ticket: ${data.title}`,
        timestamp: new Date(),
        actionUrl: `/admin/tickets/${data.ticket_id || data.ticket_uuid}`,
        data
      };
      setNotifications(prev => [notification, ...prev]);
      toast(notification.message, { duration: 5000, position: 'top-right', icon: '💬' });
    });

    // ============================================
    // Payment Events
    // ============================================
    es.addEventListener('payment_proof_uploaded', (event) => {
      const data = JSON.parse(event.data);
      const amount = data.amount ? `₫${Number(data.amount).toLocaleString('vi-VN')}` : '';
      const notification = {
        id: Date.now(),
        type: 'payment',
        title: 'Payment Proof Uploaded',
        message: `${data.renter_name || tr('notification.renterFallback')} (Room ${data.room_number || ''}) uploaded payment proof ${amount}`,
        timestamp: new Date(),
        priority: 'high',
        actionUrl: `/admin/payments/${data.payment_uuid}`,
        data
      };
      setNotifications(prev => [notification, ...prev]);
      toast.success(notification.message, { duration: 7000, position: 'top-right', icon: '💳' });
    });

    es.addEventListener('payment_reminder', (event) => {
      const data = JSON.parse(event.data);
      const notification = {
        id: Date.now(),
        type: 'payment',
        title: 'Payment Reminder',
        message: data.message,
        timestamp: new Date(),
        data
      };
      setNotifications(prev => [notification, ...prev]);
      toast(notification.message, { duration: 5000, position: 'top-right', icon: '💳' });
    });

    es.addEventListener('payment_overdue', (event) => {
      const data = JSON.parse(event.data);
      const notification = {
        id: Date.now(),
        type: 'payment',
        title: 'Payment Overdue',
        message: `Overdue payment: ${data.renter_name || tr('notification.renterFallback')} - ₫${Number(data.amount || 0).toLocaleString('vi-VN')}`,
        timestamp: new Date(),
        priority: 'urgent',
        data
      };
      setNotifications(prev => [notification, ...prev]);
      toast.error(notification.message, { duration: 10000, position: 'top-right', icon: '⚠️' });
    });

    // ============================================
    // Contract Events
    // ============================================
    es.addEventListener('contract_created', (event) => {
      const data = JSON.parse(event.data);
      const notification = {
        id: Date.now(),
        type: 'contract',
        title: 'New Contract Created',
        message: `Contract created for Room ${data.room_number || data.room_id}`,
        timestamp: new Date(),
        data
      };
      setNotifications(prev => [notification, ...prev]);
      toast.success(notification.message, { duration: 5000, position: 'top-right', icon: '📄' });
    });

    es.addEventListener('contract_expiring', (event) => {
      const data = JSON.parse(event.data);
      const notification = {
        id: Date.now(),
        type: 'contract',
        title: 'Contract Expiring',
        message: `Contract for ${data.renter_name || tr('notification.renterFallback')} expires on ${data.end_date}`,
        timestamp: new Date(),
        priority: 'high',
        data
      };
      setNotifications(prev => [notification, ...prev]);
      toast(notification.message, { duration: 7000, position: 'top-right', icon: '📅' });
    });

    es.addEventListener('contract_alert', (event) => {
      const data = JSON.parse(event.data);
      const notification = {
        id: Date.now(),
        type: 'contract',
        title: 'Contract Alert',
        message: data.message,
        timestamp: new Date(),
        data
      };
      setNotifications(prev => [notification, ...prev]);
      toast(notification.message, { duration: 5000, position: 'top-right', icon: '📄' });
    });

    // ============================================
    // Violation Events
    // ============================================
    es.addEventListener('violation_alert', (event) => {
      const data = JSON.parse(event.data);
      const notification = {
        id: Date.now(),
        type: 'violation',
        title: 'Security Alert',
        message: `${data.type} detected at ${data.location}`,
        timestamp: new Date(),
        priority: 'high',
        data
      };
      setNotifications(prev => [notification, ...prev]);
      toast.error(notification.message, { duration: 10000, position: 'top-right', icon: '🚨' });
    });

    es.addEventListener('violation_created', (event) => {
      const data = JSON.parse(event.data);
      const notification = {
        id: Date.now(),
        type: 'violation',
        title: 'New Violation Reported',
        message: `${data.type} violation: ${data.title}`,
        timestamp: new Date(),
        priority: 'high',
        actionUrl: `/admin/violations/${data.violation_uuid}`,
        data
      };
      setNotifications(prev => [notification, ...prev]);
      toast.error(notification.message, { duration: 10000, position: 'top-right', icon: '⚠️' });
    });

    es.addEventListener('violation_updated', (event) => {
      const data = JSON.parse(event.data);
      const notification = {
        id: Date.now(),
        type: 'violation',
        title: 'Violation Updated',
        message: `Violation "${data.title}" status: ${data.new_status}`,
        timestamp: new Date(),
        data
      };
      setNotifications(prev => [notification, ...prev]);
      toast(notification.message, { duration: 5000, position: 'top-right', icon: '📋' });
    });

    es.addEventListener('violation', (event) => {
      const data = JSON.parse(event.data);
      const notification = {
        id: Date.now(),
        type: 'violation',
        title: data.title || tr('notification.titleViolation'),
        message: data.message,
        timestamp: new Date(),
        priority: data.priority || 'high',
        data
      };
      setNotifications(prev => [notification, ...prev]);
      toast.error(notification.message, { duration: 7000, position: 'top-right', icon: '⚠️' });
    });

    // ============================================
    // Property/Maintenance Events
    // ============================================
    es.addEventListener('maintenance', (event) => {
      const data = JSON.parse(event.data);
      const notification = {
        id: Date.now(),
        type: 'maintenance',
        title: 'Maintenance Notice',
        message: data.message,
        timestamp: new Date(),
        data
      };
      setNotifications(prev => [notification, ...prev]);
      toast(notification.message, { duration: 5000, position: 'top-right', icon: '🔧' });
    });

    es.addEventListener('room_status_changed', (event) => {
      const data = JSON.parse(event.data);
      const notification = {
        id: Date.now(),
        type: 'property',
        title: 'Room Status Changed',
        message: `Room ${data.room_number || data.room_id} is now ${data.status}`,
        timestamp: new Date(),
        data
      };
      setNotifications(prev => [notification, ...prev]);
      toast(notification.message, { duration: 5000, position: 'top-right', icon: '🏠' });
    });

    // ============================================
    // CV/Camera Events
    // ============================================
    es.addEventListener('cv_event', (event) => {
      const data = JSON.parse(event.data);
      const notification = {
        id: Date.now(),
        type: 'cv',
        title: 'CV Detection',
        message: data.message,
        timestamp: new Date(),
        data
      };
      setNotifications(prev => [notification, ...prev]);
      toast(notification.message, { duration: 7000, position: 'top-right', icon: '📹' });
    });

    // ============================================
    // System Events
    // ============================================
    es.addEventListener('system', (event) => {
      const data = JSON.parse(event.data);
      const notification = {
        id: Date.now(),
        type: 'system',
        title: data.title || tr('notification.titleSystem'),
        message: data.message,
        timestamp: new Date(),
        data
      };
      setNotifications(prev => [notification, ...prev]);
      toast.info(notification.message, { duration: 5000, position: 'top-right' });
    });

    // Generic notification event (fallback)
    es.addEventListener('notification', (event) => {
      const data = JSON.parse(event.data);
      const notification = {
        id: Date.now(),
        type: data.type || 'system',
        title: data.title || tr('notification.titleFallback'),
        message: data.message,
        timestamp: new Date(),
        data
      };
      setNotifications(prev => [notification, ...prev]);
      toast(notification.message, { duration: 5000, position: 'top-right' });
    });

    setEventSource(es);

    // Cleanup function
    return () => {
      isCleanedUp = true;

      // Clear reconnect timeout
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }

      // Close EventSource
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }

      es.close();
      setEventSource(null);
      setIsConnected(false);
      isConnectingRef.current = false;
    };
  }, [isAuthenticated]);

  const markAsRead = (notificationId) => {
    setNotifications(prev =>
      prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
    );
  };

  const clearNotification = (notificationId) => {
    setNotifications(prev => prev.filter(n => n.id !== notificationId));
  };

  const clearAllNotifications = () => {
    setNotifications([]);
  };

  const getUnreadCount = () => {
    return notifications.filter(n => !n.read).length;
  };

  const value = {
    notifications,
    isConnected,
    markAsRead,
    clearNotification,
    clearAllNotifications,
    getUnreadCount
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

export default NotificationContext;
